// Full profile modal. Every visible control is backed by live profile state.

import { Camera, Pencil, ShieldCheck } from "lucide-react";
import { useState } from "react";

import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Button,
  Dialog,
  DialogContent,
  Input,
  Label,
  cn,
} from "@link/ui";

import { StatusDot } from "./StatusDot";
import { ProfileUsername } from "./ProfileUsername";
import { BADGES } from "../lib/badges";
import { useSelf, visibleStatus } from "../state/SelfSession";
import { profileBannerStyle } from "../lib/profile-banner";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ProfileModal({ open, onOpenChange }: Props) {
  const self = useSelf();
  const [editingName, setEditingName] = useState(false);
  const [draftName, setDraftName] = useState(self.identity.name);
  const [draftStatus, setDraftStatus] = useState(
    self.identity.customStatus ?? "",
  );
  const [draftNote, setDraftNote] = useState(self.identity.selfNote);

  const dotStatus = visibleStatus(self.status);

  const commitName = (): void => {
    const trimmed = draftName.trim();
    if (trimmed.length > 0 && trimmed !== self.identity.name) {
      self.patchIdentity({ name: trimmed });
    } else {
      setDraftName(self.identity.name);
    }
    setEditingName(false);
  };

  const commitStatus = (): void => {
    const trimmed = draftStatus.trim();
    self.patchIdentity({
      customStatus: trimmed.length === 0 ? null : trimmed,
    });
  };

  const commitNote = (): void => {
    self.patchIdentity({ selfNote: draftNote });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="grid max-h-[calc(100dvh-1rem)] max-w-[calc(100vw-1rem)] grid-cols-1 gap-0 overflow-y-auto border-grape-200 bg-white p-0 text-cloud-700 shadow-2xl shadow-grape-300/30 lg:max-w-5xl lg:grid-cols-[minmax(0,400px)_minmax(0,1fr)] lg:overflow-hidden">
        {/* ---------------- Left column ---------------- */}
        <div className="relative flex flex-col bg-grape-50/40 lg:border-r lg:border-grape-200">
          {/* Banner */}
          <div className="relative h-32 overflow-hidden">
            <div className="absolute inset-0" style={profileBannerStyle(self.identity.bannerColor, self.identity.bannerMode, self.identity.bannerSecondaryColor)} />
          </div>

          {/* Avatar overlapping the banner */}
          <div className="relative -mt-16 px-6">
            <button
              type="button"
              onClick={() => {
                onOpenChange(false);
                self.openSettings("profile");
              }}
              className="group relative inline-block rounded-full ring-[6px] ring-white transition-all hover:ring-grape-100"
              aria-label="Change avatar"
            >
              <Avatar className="h-[120px] w-[120px]">
                {self.identity.avatarUrl !== null ? (
                  <AvatarImage src={self.identity.avatarUrl} alt="" />
                ) : null}
                <AvatarFallback className="text-4xl font-semibold uppercase">
                  {self.identity.name.slice(0, 1)}
                </AvatarFallback>
              </Avatar>
              <span className="absolute inset-0 flex items-center justify-center rounded-full bg-cloud-900/0 opacity-0 transition-opacity group-hover:bg-cloud-900/45 group-hover:opacity-100">
                <Camera className="h-6 w-6 text-white" />
              </span>
              <span className="absolute bottom-2 right-2 rounded-full bg-white p-[3px]">
                <StatusDot
                  status={dotStatus}
                  sizeClass="h-4 w-4"
                  ringClass="ring-white"
                />
              </span>
            </button>

            {self.identity.customStatus !== null ? (
              <div className="absolute left-[150px] top-2 max-w-[220px] rounded-2xl rounded-bl-sm bg-white px-3 py-1.5 text-xs italic text-cloud-700 shadow ring-1 ring-grape-200">
                {self.identity.customStatus}
              </div>
            ) : null}
          </div>

          <div className="flex flex-1 flex-col gap-5 px-6 pb-6 pt-4">
            {/* Name + tag */}
            <div>
              {editingName ? (
                <form
                  className="flex items-center gap-2"
                  onSubmit={(e) => {
                    e.preventDefault();
                    commitName();
                  }}
                >
                  <Input
                    autoFocus
                    value={draftName}
                    onChange={(e) => setDraftName(e.target.value)}
                    onBlur={commitName}
                    className="h-9 border-grape-200 bg-white text-cloud-800 placeholder:text-cloud-400"
                  />
                </form>
              ) : (
                <button
                  type="button"
                  onClick={() => setEditingName(true)}
                  className="group inline-flex items-center gap-2 rounded-md text-2xl font-semibold text-cloud-800 hover:text-strawberry-600"
                >
                  <span>{self.identity.name}</span>
                  <Pencil className="h-3.5 w-3.5 text-cloud-400 opacity-0 transition-opacity group-hover:opacity-100" />
                </button>
              )}
              <ProfileUsername username={self.identity.username} badges={self.identity.badges ?? []}>
                {self.identity.pronouns !== null ? <span className="rounded-full bg-cloud-100 px-2 py-0.5 text-[10px] font-semibold text-cloud-600 ring-1 ring-cloud-200">{self.identity.pronouns}</span> : null}
                {self.identity.communityTag !== null ? <span className="rounded-md bg-grape-100 px-1.5 py-0.5 text-[10px] font-bold text-grape-700 ring-1 ring-grape-200" title={self.identity.communityTag.space_name}>{self.identity.communityTag.code}</span> : null}
              </ProfileUsername>
              <p className="mt-1 inline-flex items-center gap-1.5 text-[11px] font-medium text-cloud-500">
                <StatusDot
                  status={dotStatus}
                  sizeClass="h-2 w-2"
                  ringClass="ring-white"
                />
                {dotStatus.charAt(0).toUpperCase() + dotStatus.slice(1)}
              </p>
            </div>

            <div>
              <Button
                variant="secondary"
                className="border-none bg-strawberry-gradient text-white hover:opacity-95"
                onClick={() => {
                  onOpenChange(false);
                  self.setSettingsOpen(true);
                }}
              >
                <Pencil className="h-4 w-4" />
                Edit Profile
              </Button>
            </div>

            {/* Custom status */}
            <div>
              <Label
                htmlFor="custom-status-modal"
                className="text-[11px] font-semibold uppercase tracking-wider text-cloud-500"
              >
                Custom status
              </Label>
              <Input
                id="custom-status-modal"
                value={draftStatus}
                onChange={(e) => setDraftStatus(e.target.value)}
                onBlur={commitStatus}
                placeholder="What are you up to?"
                className="mt-1 h-9 border-grape-200 bg-white text-cloud-800 placeholder:text-cloud-400"
              />
            </div>

            {/* Badges */}
            {self.identity.badges !== undefined &&
              self.identity.badges.length > 0 && (
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-cloud-500">
                    Badges
                  </p>
                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                    {self.identity.badges.map((id) => {
                      const meta = BADGES[id];
                      return (
                        <span
                          key={id}
                          className="inline-flex items-center gap-1.5 rounded-full bg-cloud-50 px-2.5 py-1 text-[11px] font-medium text-cloud-700 ring-1 ring-cloud-200"
                        >
                          <span
                            className={cn("inline-flex", meta.toneClass)}
                            style={{ color: meta.toneColor }}
                          >
                            {meta.icon("h-3 w-3")}
                          </span>
                          {meta.label}
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}

            {/* Member since + Self-note in a 2-col layout to use the
                width better. */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wider text-cloud-500">
                  Member since
                </p>
                <p className="mt-1 text-sm text-cloud-700">
                  {formatMemberSince(self.identity.memberSince)}
                </p>
              </div>
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wider text-cloud-500">
                  Username
                </p>
                <p className="mt-1 truncate text-sm text-cloud-700">
                  @{self.identity.username}
                </p>
              </div>
            </div>

            {/* Self-note */}
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wider text-cloud-500">
                Note (only visible to you)
              </p>
              <textarea
                value={draftNote}
                onChange={(e) => setDraftNote(e.target.value)}
                onBlur={commitNote}
                rows={2}
                placeholder="Click to add a note"
                className="mt-1 w-full resize-none rounded-md bg-transparent px-1 py-1 text-sm text-cloud-700 outline-none placeholder:text-cloud-400 hover:bg-white focus:bg-white"
              />
            </div>
          </div>
        </div>

        <div className="flex max-h-[80vh] flex-col overflow-y-auto px-5 pb-8 pt-8 sm:px-8 sm:pt-12">
          <div className="flex items-start justify-between gap-4 border-b border-cloud-200 pb-5">
            <div>
              <h3 className="text-lg font-semibold text-cloud-800">Profile overview</h3>
              <p className="mt-1 text-sm text-cloud-500">
                This is how your identity appears across Link.
              </p>
            </div>
            <Button
              variant="outline"
              onClick={() => {
                onOpenChange(false);
                self.openSettings("profile");
              }}
            >
              <Pencil className="h-4 w-4" />
              Edit
            </Button>
          </div>

          <dl className="mt-6 grid gap-3 sm:grid-cols-2">
            <ProfileDetail label="Display name" value={self.identity.name} />
            <ProfileDetail label="Username" value={`@${self.identity.username}`} />
            <ProfileDetail
              label="Status"
              value={dotStatus.charAt(0).toUpperCase() + dotStatus.slice(1)}
            />
            <ProfileDetail label="Pronouns" value={self.identity.pronouns ?? "Not set"} />
            <ProfileDetail
              label="Community tag"
              value={self.identity.communityTag?.code ?? "Not equipped"}
            />
            <ProfileDetail
              label="Member since"
              value={formatMemberSince(self.identity.memberSince)}
            />
          </dl>

          <div className="mt-6 rounded-2xl border border-cloud-200 bg-cloud-50/60 p-5">
            <div className="flex items-center gap-2 text-cloud-800">
              <ShieldCheck className="h-5 w-5 text-strawberry-600" />
              <p className="font-semibold">Key-based identity</p>
            </div>
            <p className="mt-2 text-sm leading-6 text-cloud-500">
              Your account is authenticated with your local identity key. Link never needs an email address or password to verify you.
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ProfileDetail({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl border border-cloud-200 bg-cloud-50/60 px-4 py-3">
      <dt className="text-[10px] font-semibold uppercase tracking-wider text-cloud-400">
        {label}
      </dt>
      <dd className="mt-1 truncate text-sm font-medium text-cloud-700">{value}</dd>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helper
// ---------------------------------------------------------------------------

function formatMemberSince(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}
